import React, { useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { IconButton } from "@mui/material";
import EditableTextCell from "./EditableTextCell";
import ActionRiskValueView from "./ActionRiskValueView";

import styles from "../../../v2Styles.module.css";

export default function ActivityActions({ action, actionIndex, actIndex, actId, activityNumber, totalActions, onUpdate, onDelete, onAdd, onMove, actionCount, onDrop, likelihoods, allConsequences, riskRatings, onRiskSave, isDisabled }) {
  const previewRef = useRef(null);
  const dragHandleRef = useRef(null);

  let hazards = [];
  if (action && action.fm_swms_activities_hazards_v2 && Array.isArray(action.fm_swms_activities_hazards_v2)) {
    hazards = action.fm_swms_activities_hazards_v2;
  }

  const stepLabel = activityNumber + "." + (actionIndex + 1);

  let actionRows = 1;
  let hazardRowCounts = [];

  if (hazards.length) {
    actionRows = 0;
    hazardRowCounts = [];

    for (let hi = 0; hi < hazards.length; hi++) {
      let hazard = hazards[hi];
      let consequences = [];

      if (hazard && hazard.fm_swms_hazards_consequences_v2 && Array.isArray(hazard.fm_swms_hazards_consequences_v2)) {
        consequences = hazard.fm_swms_hazards_consequences_v2;
      }

      let rowsForHazard = consequences.length > 0 ? consequences.length : 1;

      hazardRowCounts.push(rowsForHazard);
      actionRows += rowsForHazard;
    }
  }

  const [, drop] = useDrop({
    accept: `activity_actions_${actIndex}`,
    hover: (item, monitor) => {
      if (!previewRef.current) return;
      if (item.actIndex !== actIndex) return;
      if (item.actionIndex === actionIndex) return;

      const rect = previewRef.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - rect.top;

      if (item.actionIndex < actionIndex && hoverClientY < middleY) return;
      if (item.actionIndex > actionIndex && hoverClientY > middleY) return;

      onMove({
        type: "action",
        actIndex,
        fromIndex: item.actionIndex,
        toIndex: actionIndex,
      });

      item.actionIndex = actionIndex;
    },
    drop: () => ({ dropped: true }),
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: `activity_actions_${actIndex}`,
    item: () => ({
      actIndex,
      actionIndex,
      originalActionIndex: actionIndex,
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) return;

      if (item.originalActionIndex !== item.actionIndex) {
        onDrop(actId);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (previewRef.current) {
      preview(previewRef.current);
      drop(previewRef.current);
    }

    if (dragHandleRef.current) {
      drag(dragHandleRef.current);
    }
  }, [preview, drop, drag]);

  if (!hazards.length) {
    let actionName = "";
    let initialRisk = "";
    let residualRisk = "";
    let controlMeasures = "";
    let persons = "";

    if (action) {
      if (action.action_name != null) { actionName = action.action_name; }
      if (action.initial_risk != null) { initialRisk = action.initial_risk; }
      if (action.residual_risk != null) { residualRisk = action.residual_risk; }
      if (action.control_measures != null) { controlMeasures = action.control_measures; }
      if (action.persons_in_control != null) { persons = action.persons_in_control; }
    }

    return (
      <tr
        ref={previewRef}
        className="preview-swms-activity-header-row"
        style={{ opacity: isDragging ? 0.45 : 1 }}
      >
        <td className="activity-action-step-cell position-relative">
          <span>{stepLabel}</span>
          {!isDisabled && actionCount > 1 ? (
            <div className="position-absolute" style={{ top: "5px", right: "2px" }}>
              <IconButton ref={dragHandleRef} onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} className={`${styles.IconBtn} ${styles.SecondaryColor} p-0 activity-drag-icon`} style={{ height: "auto", background: "#fff" }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ height: "16px", width: "16px" }}>
                  <path fill="currentColor" d="M288 104C288 81.9 270.1 64 248 64L200 64C177.9 64 160 81.9 160 104L160 152C160 174.1 177.9 192 200 192L248 192C270.1 192 288 174.1 288 152L288 104zM288 296C288 273.9 270.1 256 248 256L200 256C177.9 256 160 273.9 160 296L160 344C160 366.1 177.9 384 200 384L248 384C270.1 384 288 366.1 288 344L288 296zM160 488L160 536C160 558.1 177.9 576 200 576L248 576C270.1 576 288 558.1 288 536L288 488C288 465.9 270.1 448 248 448L200 448C177.9 448 160 465.9 160 488zM480 104C480 81.9 462.1 64 440 64L392 64C369.9 64 352 81.9 352 104L352 152C352 174.1 369.9 192 392 192L440 192C462.1 192 480 174.1 480 152L480 104zM352 296L352 344C352 366.1 369.9 384 392 384L440 384C462.1 384 480 366.1 480 344L480 296C480 273.9 462.1 256 440 256L392 256C369.9 256 352 273.9 352 296zM480 488C480 465.9 462.1 448 440 448L392 448C369.9 448 352 465.9 352 488L352 536C352 558.1 369.9 576 392 576L440 576C462.1 576 480 558.1 480 536L480 488z" />
                </svg>
              </IconButton>
            </div>
          ) : null}
        </td>

        <EditableTextCell
          showAddIcon={true}
          isDisabled={isDisabled}
          showDeleteIcon={totalActions > 1}
          onDelete={() => onDelete({ type: "action", actIndex, actionIndex })}
          onAdd={() => onAdd({ type: "hazard", actIndex, actionIndex })}
          onUpdate={onUpdate}
          dataObj={{ actIndex, actionIndex, key: "action_name" }}
          value={actionName}
        />

        <td></td>
        <td></td>

        <td className="text-center" style={{ background: action.irRisk }}>
          {initialRisk}
        </td>

        <EditableTextCell
          onUpdate={onUpdate}
          isDisabled={isDisabled}
          dataObj={{ actIndex, actionIndex, key: "control_measures" }}
          containerStyle={{ whiteSpace: "pre", textWrap: "wrap" }}
          value={controlMeasures}
          multiLine={true}
        />

        <td className="text-center" style={{ background: action.rrRisk }}>
          {residualRisk}
        </td>

        <EditableTextCell
          onUpdate={onUpdate}
          isDisabled={isDisabled}
          dataObj={{ actIndex, actionIndex, key: "persons_in_control" }}
          value={persons}
        />
      </tr>
    );
  }

  let rows = [];
  let actionCellWritten = false;

  for (let hi = 0; hi < hazards.length; hi++) {
    let hazard = hazards[hi];
    let consequences = [];

    if (
      hazard &&
      hazard.fm_swms_hazards_consequences_v2 &&
      Array.isArray(hazard.fm_swms_hazards_consequences_v2)
    ) {
      consequences = hazard.fm_swms_hazards_consequences_v2;
    }

    let rowsForHazard = hazardRowCounts[hi];

    for (let ci = 0; ci < rowsForHazard; ci++) {
      let firstActionRow = !actionCellWritten;
      let firstHazardRow = ci === 0;

      let consequenceName = "";
      if (consequences.length > 0 && consequences[ci] && consequences[ci].consequences_name != null) {
        consequenceName = consequences[ci].consequences_name;
      }

      rows.push(
        <tr
          key={"a-" + actionIndex + "-h-" + hi + "-c-" + ci}
          ref={firstActionRow ? previewRef : null}
          className={firstActionRow ? "preview-swms-activity-header-row" : ""}
          style={{ opacity: isDragging ? 0.45 : 1 }}
        >
          {firstActionRow ? (
            <td rowSpan={actionRows} className="activity-action-step-cell position-relative">
              <span>{stepLabel}</span>
              {!isDisabled && actionCount > 1 ? (
                <div className="position-absolute" style={{ top: "5px", right: "2px" }}>
                  <IconButton ref={dragHandleRef} onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} className={`${styles.IconBtn} ${styles.SecondaryColor} p-0 activity-drag-icon`} style={{ height: "auto", background: "#fff" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ height: "16px", width: "16px" }}>
                      <path fill="currentColor" d="M288 104C288 81.9 270.1 64 248 64L200 64C177.9 64 160 81.9 160 104L160 152C160 174.1 177.9 192 200 192L248 192C270.1 192 288 174.1 288 152L288 104zM288 296C288 273.9 270.1 256 248 256L200 256C177.9 256 160 273.9 160 296L160 344C160 366.1 177.9 384 200 384L248 384C270.1 384 288 366.1 288 344L288 296zM160 488L160 536C160 558.1 177.9 576 200 576L248 576C270.1 576 288 558.1 288 536L288 488C288 465.9 270.1 448 248 448L200 448C177.9 448 160 465.9 160 488zM480 104C480 81.9 462.1 64 440 64L392 64C369.9 64 352 81.9 352 104L352 152C352 174.1 369.9 192 392 192L440 192C462.1 192 480 174.1 480 152L480 104zM352 296L352 344C352 366.1 369.9 384 392 384L440 384C462.1 384 480 366.1 480 344L480 296C480 273.9 462.1 256 440 256L392 256C369.9 256 352 273.9 352 296zM480 488C480 465.9 462.1 448 440 448L392 448C369.9 448 352 465.9 352 488L352 536C352 558.1 369.9 576 392 576L440 576C462.1 576 480 558.1 480 536L480 488z" />
                    </svg>
                  </IconButton>
                </div>
              ) : null}
            </td>
          ) : null}

          {firstActionRow ? (
            <EditableTextCell
              showDeleteIcon={totalActions > 1}
              showAddIcon={true}
              isDisabled={isDisabled}
              onDelete={() => onDelete({ type: "action", actIndex, actionIndex })}
              onAdd={() => onAdd({ type: "hazard", actIndex, actionIndex })}
              onUpdate={onUpdate}
              dataObj={{ actIndex, actionIndex, key: "action_name" }}
              value={action && action.action_name != null ? action.action_name : ""}
              rowSpan={actionRows}
            />
          ) : null}

          {firstHazardRow ? (
            <EditableTextCell
              isDisabled={isDisabled}
              showDeleteIcon={hazards.length > 1}
              onDelete={() => onDelete({ type: "hazard", actIndex, actionIndex, hazardIndex: hi })}
              showMoveIcons={hazards.length > 1}
              showDownArrow={hi === hazards.length - 1 ? false : true}
              showUpArrow={hi === 0 ? false : true}
              showAddIcon={true}
              onAdd={() => onAdd({ type: "consequence", actIndex, actionIndex, hazardIndex: hi })}
              onUpdate={onUpdate}
              onMove={(direction) => { onMove({ type: "hazard", actIndex, actionIndex, fromIndex: hi, toIndex: direction === "up" ? hi - 1 : hi + 1 }) }}
              dataObj={{ actIndex, actionIndex, hazardIndex: hi, key: "hazard_name" }}
              value={hazard && hazard.hazard_name != null ? hazard.hazard_name : ""}
              rowSpan={rowsForHazard}
            />
          ) : null}

          <EditableTextCell
            onDelete={() => onDelete({ type: "consequence", actIndex, actionIndex, hazardIndex: hi, consIndex: ci })}
            showDeleteIcon={consequences.length > 1}
            showMoveIcons={consequences.length > 1}
            isDisabled={isDisabled}
            showDownArrow={ci === consequences.length - 1 ? false : true}
            showUpArrow={ci === 0 ? false : true}
            onMove={(direction) => { onMove({ type: "consequence", actIndex, actionIndex, hazardIndex: hi, fromIndex: ci, toIndex: direction === "up" ? ci - 1 : ci + 1 }) }}
            onUpdate={onUpdate}
            dataObj={{ actIndex, actionIndex, hazardIndex: hi, consequenceIndex: ci, key: "consequences_name" }}
            value={consequenceName}
          />

          {firstActionRow ? (
            <ActionRiskValueView
              likelihoods={likelihoods}
              consequences={allConsequences}
              riskType="before"
              actIndex={actIndex}
              isDisabled={isDisabled}
              actionIndex={actionIndex}
              riskRatings={riskRatings}
              initialCons={action.before_consequence ? action.before_consequence : null}
              initialLike={action.before_likelihood ? action.before_likelihood : null}
              label="Before Controls"
              onRiskSave={onRiskSave}
              value={action && action.initial_risk != null ? action.initial_risk : ""}
              bgColor={action.irRisk}
              rowSpan={actionRows}
            />
          ) : null}

          {firstActionRow ? (
            <EditableTextCell
              onUpdate={onUpdate}
              isDisabled={isDisabled}
              dataObj={{ actIndex, actionIndex, key: "control_measures" }}
              value={action && action.control_measures != null ? action.control_measures : ""}
              rowSpan={actionRows}
              multiLine={true}
            />
          ) : null}

          {firstActionRow ? (
            <ActionRiskValueView
              likelihoods={likelihoods}
              riskType="after"
              isDisabled={isDisabled}
              actIndex={actIndex}
              actionIndex={actionIndex}
              consequences={allConsequences}
              riskRatings={riskRatings}
              initialCons={action.after_consequence ? action.after_consequence : null}
              initialLike={action.after_likelihood ? action.after_likelihood : null}
              label="After Controls"
              onRiskSave={onRiskSave}
              value={action.residual_risk ? action.residual_risk : ""}
              bgColor={action.rrRisk}
              rowSpan={actionRows} />
          ) : null}

          {firstActionRow ? (
            <EditableTextCell
              onUpdate={onUpdate}
              isDisabled={isDisabled}
              dataObj={{ actIndex, actionIndex, key: "persons_in_control", }}
              value={action && action.persons_in_control != null ? action.persons_in_control : ""}
              rowSpan={actionRows}
            />
          ) : null}
        </tr>
      );

      actionCellWritten = true;
    }
  }

  return rows;
}
